/**
 * Der Abschnitt **Kontrollmuster** (ADR-0033): alle Muster mit Kennung,
 * Erkennung, Name, Art, Schwere und Zustand.
 *
 * **Gepflegt wird die Aussage eines Musters und, wo sie in den Daten lebt, auch
 * seine Erkennung.** Name, Meldungstext, Befundart und Schweregrad sind bei
 * jedem Muster änderbar. Bei einem **Fragmentmuster** kommt das Codefragment
 * dazu: Seine Erkennung ist eine Datenzeile, und ein neues Muster ist deshalb
 * kein Ausrollen mehr. Bei einem **Sniffmuster** bleibt der Meldungscode
 * Auskunft — dahinter steht eine Klasse im Motor. Die Kennung ist bei beiden
 * Auskunft: Sie steht an jedem Befund, den dieses Muster je erzeugt hat, und
 * wird so wenig umbenannt wie eine Tabelle.
 *
 * **Das Beispielpaar ist der Test des Musters** (Glossar, „Beispielpaar"): ein
 * Schnipsel, den es treffen muss, und einer, den es nicht treffen darf. Bei
 * einem Fragmentmuster sind beide Pflicht; der Server fährt sie, **bevor** er
 * speichert, und weist zurück, was nicht hält — samt der Angabe, welcher der
 * beiden es war. Bei einem Sniffmuster ist das Paar freiwillig: Dort ist der
 * Sniff samt seinem Test im Repository der Test.
 *
 * **Angelegt wird ein Fragmentmuster, gelöscht wird nichts.** Ein Sniffmuster
 * hier anzulegen ergäbe eine Zeile ohne Sniff, die nie trifft. Und ein Muster,
 * das nicht mehr soll, wird **stillgelegt**: Seine erhobenen Befunde bleiben
 * stehen und behalten ihren Status — das Stilllegen schreibt die Vergangenheit
 * nicht um.
 *
 * **Ein stillgelegtes Muster bleibt in der Liste** und ist als stillgelegt
 * erkennbar. Es zu verstecken hieße, das Wiedereinschalten unmöglich zu machen,
 * und die Frage „warum meldet das nichts mehr?" bliebe ohne Antwort — genau die
 * beantworten Zeitpunkt, Person und Begründung in der Zeile.
 *
 * **Die Begründung ist Pflicht, und geprüft wird sie im Server.** Hier steht nur
 * ihre Antwort und der Knopf, der ohne Text nicht auslöst: Zwei Prüfungen wären
 * zwei Auslegungen desselben Satzes.
 *
 * **Kein Bestandslauf als Nebenwirkung.** Weder Stilllegen noch
 * Wiedereinschalten noch eine Textänderung stößt einen an; wer den Altbestand
 * mit einem wieder eingeschalteten Muster sehen will, stößt den Lauf in der
 * Projektmaske an. Das sagt der Abschnitt an Ort und Stelle, denn es ist die
 * Erwartung, die man hier mitbringt.
 *
 * **Und deshalb steht neben der Zahl der Befunde, ob ein Bestandslauf gefahren
 * wurde.** Ein Muster, das aus einem Befund entstanden ist, findet ab seiner
 * Entstehung und hat den Altbestand nie gesehen; ohne diese Angabe läse sich
 * seine Null als Entwarnung, die niemand gegeben hat. Aus welchem Befund es
 * entstand, steht als Auskunft am Namen — das ist die Antwort auf „warum gibt es
 * dieses Muster", und die wird in sechs Monaten gestellt (ADR-0033, ADR-0012).
 */

import { useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  IconButton,
  Modal,
  Select,
  StatusPill,
  TextField,
} from "../../design-system";
import type { AdminApi, ControlPattern, ControlPatternDraft } from "../../api/client";
import { StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";
import { MultilineRow, NewPatternDialog } from "../pattern/NewPatternDialog";

export function ControlPatternsSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded(() => client.controlPatterns());
  const patterns = state.kind === "loaded" ? state.value.patterns : [];
  const kinds = state.kind === "loaded" ? state.value.kinds : [];
  const severities = state.kind === "loaded" ? state.value.severities : [];
  const [edited, setEdited] = useState<ControlPattern | null>(null);
  const [retired, setRetired] = useState<ControlPattern | null>(null);
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const act = (action: Promise<unknown>): void => {
    setFailure(null);
    action.then(reload, (error: unknown) => setFailure(describe(error)));
  };

  const active = patterns.filter((one) => one.active).length;

  return (
    <>
      <ListSection
        title="Kontrollmuster"
        glyph="scan-search"
        first
        headerRight={
          state.kind === "loaded" ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
              <Chip>{`${active} von ${patterns.length} aktiv`}</Chip>
              <Button variant="outline" size="sm" icon="plus" onClick={() => setCreating(true)}>
                Neues Muster
              </Button>
            </span>
          ) : null
        }
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-3)",
            padding: "var(--sp-2) var(--sp-5)",
          }}
        >
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {failure === null ? null : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}
          {state.kind === "loaded" ? (
            <Notice
              text="Ein neues Muster bringt seine Erkennung als Codefragment und sein Beispielpaar mit; gespeichert wird nur, was das Paar besteht. Text, Art und Schwere gelten ab dem nächsten Fund; erhobene Befunde bleiben, wie sie sind. Stilllegen und Wiedereinschalten stoßen keinen Bestandslauf an."
              glyph="circle-alert"
            />
          ) : null}
        </div>

        {state.kind === "loaded" && patterns.length > 0 ? (
          <DataTable
            columns={[
              { key: "label", label: "Name", width: 220, maxWidth: 220 },
              // Die Kennung wird abgetippt nachgelesen und nicht überflogen:
              // Sie steht an jedem Befund dieses Musters.
              { key: "identifier", label: "Kennung", width: 200, maxWidth: 200 },
              // Die Erkennungsart steht in der Liste, weil sie sagt, was sich an
              // diesem Muster überhaupt ändern lässt — und nicht, weil ein
              // Befund davon abhinge: Die Befunde beider Arten sind
              // ununterscheidbar.
              { key: "detection", label: "Erkennung", width: 120 },
              { key: "kind", label: "Art", width: 130 },
              { key: "severity", label: "Schwere", width: 100 },
              // Was dieses Muster gefunden hat — und ob es je über einen
              // Bestand gelaufen ist. Die zweite Angabe trägt die erste: Ohne
              // sie läse sich die Null eines frisch angelegten Musters als
              // Entwarnung, die niemand gegeben hat (ADR-0033).
              { key: "found", label: "Befunde", width: 180 },
              { key: "state", label: "Zustand" },
              { key: "actions", label: "", width: 70, align: "right" },
            ]}
            rows={patterns.map((pattern) => ({
              cells: {
                label: (
                  <StrongCell title={titleOf(pattern)}>{pattern.label}</StrongCell>
                ),
                identifier: <span title={detectionOf(pattern)}>{pattern.identifier}</span>,
                detection: <span title={detectionOf(pattern)}>{pattern.detection}</span>,
                kind: pattern.kind,
                severity: pattern.severity,
                found: <FoundCell pattern={pattern} />,
                state: <StateCell pattern={pattern} />,
                actions: (
                  <span style={{ display: "inline-flex", gap: "var(--sp-3)" }}>
                    <IconButton
                      icon="pencil"
                      tip="Ändern"
                      size="sm"
                      tone="quiet"
                      onClick={() => setEdited(pattern)}
                    />
                    {pattern.active ? (
                      <IconButton
                        icon="lock"
                        tip="Stilllegen"
                        size="sm"
                        tone="quiet"
                        onClick={() => setRetired(pattern)}
                      />
                    ) : (
                      <IconButton
                        icon="lock-open"
                        tip="Wieder einschalten"
                        size="sm"
                        tone="quiet"
                        onClick={() => act(client.reinstateControlPattern(pattern.id))}
                      />
                    )}
                  </span>
                ),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {edited === null ? null : (
        <PatternDialog
          pattern={edited}
          kinds={kinds}
          severities={severities}
          save={(draft) => client.updateControlPattern(edited.id, draft)}
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}

      {creating ? (
        <NewPatternDialog
          kinds={kinds}
          severities={severities}
          create={(draft) => client.createControlPattern(draft)}
          close={() => setCreating(false)}
          created={() => {
            setCreating(false);
            reload();
          }}
        />
      ) : null}

      {retired === null ? null : (
        <RetirementDialog
          pattern={retired}
          retire={(reason) => client.retireControlPattern(retired.id, reason)}
          close={() => setRetired(null)}
          retired={() => {
            setRetired(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/**
 * Womit dieses Muster erkennt — der Sniff im Motor oder das Codefragment.
 *
 * Steht als Titel an der Zeile und nicht als Spalte: Ein Meldungscode und ein
 * Codefragment sind beide zu lang für eine Liste, und gebraucht werden sie beim
 * Nachsehen und nicht beim Überfliegen.
 */
function detectionOf(pattern: ControlPattern): string {
  return pattern.fragment ?? pattern.sniffCode ?? "";
}

/** Was „kein Bestandslauf" heißt — ausgeschrieben am Chip. */
const WITHOUT_INVENTORY_RUN =
  "Dieses Muster ist über keinen Bestandslauf gegangen: Es findet ab seiner Entstehung, den "
  + "Altbestand hat es nie gesehen. Ein Bestandslauf wird in der Projektmaske angestoßen.";

/**
 * Was dieses Muster gefunden hat — und ob es je über einen **Bestandslauf**
 * gegangen ist.
 *
 * Beides steht nebeneinander und nicht verrechnet, denn erst zusammen sagen sie
 * etwas: „keine Befunde" heißt bei einem Muster, das über den Altbestand
 * gelaufen ist, dass es dort nichts gibt — bei einem frisch angelegten heißt es,
 * dass niemand nachgesehen hat. Ohne diese Unterscheidung wäre die Null eine
 * Entwarnung, die niemand gegeben hat (ADR-0033).
 *
 * Ein Bestandslauf entsteht daraus nicht: Er bleibt ein ausdrücklich angestoßener
 * Vorgang in der Projektmaske. Hier steht die Auskunft und kein Knopf.
 */
function FoundCell({ pattern }: { pattern: ControlPattern }): ReactElement {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-3)" }}>
      <span style={{ fontSize: "var(--fs-12)", fontVariantNumeric: "tabular-nums" }}>
        {pattern.findingCount === 0 ? "keine Befunde" : `${pattern.findingCount} Befunde`}
      </span>
      {pattern.inventoried ? null : (
        // Der ganze Satz steht am umgebenden Element: Die Pille selbst führt
        // keinen Titel, und nachgebaut wird sie dafür nicht.
        <span title={WITHOUT_INVENTORY_RUN} style={{ display: "inline-flex" }}>
          <Chip>kein Bestandslauf</Chip>
        </span>
      )}
    </span>
  );
}

/**
 * Was am Namen im Titel steht: der Meldungstext, und wo es einen gibt, der
 * **Ursprungsbefund**.
 *
 * Er steht als Auskunft und nicht als Spalte: Gefragt wird „warum gibt es dieses
 * Muster", und das fragt man beim Nachsehen und nicht beim Überfliegen.
 */
function titleOf(pattern: ControlPattern): string {
  const origin = pattern.originFinding;

  if (origin === null) {
    return pattern.message;
  }

  const lines =
    origin.startLine === origin.endLine
      ? `Zeile ${origin.startLine}`
      : `Zeilen ${origin.startLine}–${origin.endLine}`;

  return `${pattern.message}\n\nAus einem Befund an ${origin.path} · r${origin.revisionNumber} · ${lines}`;
}

/**
 * Der Zustand eines Musters: aktiv, oder stillgelegt samt Grund.
 *
 * Der Grund steht daneben und nicht hinter einem Knopf: Er ist die Antwort auf
 * „warum meldet das nichts mehr?", und die will man beim Überfliegen der Liste
 * haben. Wer stillgelegt hat und wann, steht im Titel — das ist die zweite
 * Frage und nicht die erste.
 */
function StateCell({ pattern }: { pattern: ControlPattern }): ReactElement {
  if (pattern.active) {
    return <StatusPill tone="green">aktiv</StatusPill>;
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
      <Chip>stillgelegt</Chip>
      <span
        title={retirementTitle(pattern)}
        style={{
          fontSize: "var(--fs-11)",
          color: "var(--fg-6)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {pattern.retiredReason ?? ""}
      </span>
    </span>
  );
}

/**
 * Wer wann stillgelegt hat.
 *
 * Ohne Person steht nur der Zeitpunkt da: Die Saat der Migration hat für
 * niemanden gehandelt, und eine erfundene Systemperson wäre eine Behauptung über
 * einen Menschen, den es nicht gibt.
 */
function retirementTitle(pattern: ControlPattern): string {
  const when = pattern.retiredAt === null ? "" : new Date(pattern.retiredAt).toLocaleString("de-DE");
  const who = pattern.retiredByName;

  if (who === null) {
    return when === "" ? "" : `Stillgelegt am ${when}`;
  }

  return when === "" ? `Stillgelegt von ${who}` : `Stillgelegt von ${who} am ${when}`;
}

/**
 * Ein Muster ändern — Name, Meldungstext, Art, Schwere, das Beispielpaar und bei
 * einem Fragmentmuster das Codefragment.
 *
 * Die Kennung steht als Auskunft da: Sie steht an jedem Befund dieses Musters.
 * Bei einem Sniffmuster steht der Meldungscode daneben und ebenfalls als
 * Auskunft — dahinter liegt eine Klasse im Motor, und die kommt mit einem
 * Release.
 */
function PatternDialog({
  pattern,
  kinds,
  severities,
  save,
  close,
  saved,
}: {
  pattern: ControlPattern;
  kinds: string[];
  severities: string[];
  save: (draft: ControlPatternDraft) => Promise<unknown>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [label, setLabel] = useState(pattern.label);
  const [message, setMessage] = useState(pattern.message);
  const [kind, setKind] = useState(pattern.kind);
  const [severity, setSeverity] = useState(pattern.severity);
  const [fragment, setFragment] = useState(pattern.fragment ?? "");
  const [positive, setPositive] = useState(pattern.positiveExample ?? "");
  const [negative, setNegative] = useState(pattern.negativeExample ?? "");
  const [failure, setFailure] = useState<string | null>(null);

  const isFragment = pattern.detection === "fragment";

  return (
    <Modal onClose={close}>
      <Dialog
        title="Kontrollmuster ändern"
        description={
          isFragment
            ? "Fragment und Beispielpaar werden geprüft, bevor gespeichert wird. Name, Meldungstext, Befundart und Schweregrad gelten ab dem nächsten Fund; die Kennung steht fest."
            : "Name, Meldungstext, Befundart und Schweregrad gelten ab dem nächsten Fund. Die Kennung und der Sniff, der erkennt, stehen fest. Ein Beispielpaar ist hier freiwillig — steht eines da, wird es nachgefahren."
        }
        width={640}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                setFailure(null);
                save({
                  label,
                  message,
                  kind,
                  severity,
                  ...(isFragment ? { fragment } : {}),
                  positiveExample: positive,
                  negativeExample: negative,
                }).then(saved, (error: unknown) => setFailure(describe(error)));
              }}
            >
              Speichern
            </Button>
          </>
        }
      >
        <FormGrid>
          <FormRow label="Kennung">
            <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-6)" }}>
              {pattern.identifier}
            </span>
          </FormRow>
          {isFragment ? (
            <MultilineRow
              id="control-pattern-fragment"
              label="Codefragment"
              value={fragment}
              onChange={setFragment}
              mono
            />
          ) : (
            <FormRow label="Erkennung">
              <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-6)" }}>
                {pattern.sniffCode}
              </span>
            </FormRow>
          )}
          <FormRow label="Name" htmlFor="control-pattern-label">
            <TextField
              id="control-pattern-label"
              value={label}
              placeholder="Zuweisung in einer Bedingung"
              onChange={setLabel}
            />
          </FormRow>
          <FormRow label="Befundart" htmlFor="control-pattern-kind">
            <Select options={kinds} value={kind} onSelect={setKind} />
          </FormRow>
          <FormRow label="Schweregrad" htmlFor="control-pattern-severity">
            <Select options={severities} value={severity} onSelect={setSeverity} />
          </FormRow>
          <MultilineRow
            id="control-pattern-message"
            label="Meldungstext"
            value={message}
            onChange={setMessage}
          />
          <MultilineRow
            id="control-pattern-positive"
            label="Positivbeispiel"
            value={positive}
            onChange={setPositive}
            mono
          />
          <MultilineRow
            id="control-pattern-negative"
            label="Negativbeispiel"
            value={negative}
            onChange={setNegative}
            mono
          />
        </FormGrid>

        {failure === null ? null : (
          <div style={{ paddingTop: "var(--sp-4)" }}>
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          </div>
        )}
      </Dialog>
    </Modal>
  );
}

/**
 * Stilllegen — mit Pflichtbegründung.
 *
 * Der Knopf löst ohne Text nicht aus. Das ersetzt die Prüfung im Server nicht,
 * es erspart nur die Fehlermeldung für den offensichtlichen Fall; ein Grund aus
 * Leerzeichen wird dort abgewiesen.
 */
function RetirementDialog({
  pattern,
  retire,
  close,
  retired,
}: {
  pattern: ControlPattern;
  retire: (reason: string) => Promise<unknown>;
  close: () => void;
  retired: () => void;
}): ReactElement {
  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState<string | null>(null);

  return (
    <Modal onClose={close}>
      <Dialog
        title="Kontrollmuster stilllegen"
        description={`„${pattern.label}" findet danach nichts mehr. Die bereits erhobenen Befunde bleiben stehen und behalten ihren Status; ein Bestandslauf entsteht daraus nicht. Wieder einschalten geht jederzeit.`}
        width={560}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={reason.trim() === ""}
              onClick={() => {
                setFailure(null);
                retire(reason).then(retired, (error: unknown) => setFailure(describe(error)));
              }}
            >
              Stilllegen
            </Button>
          </>
        }
      >
        <FormGrid>
          <MultilineRow
            id="control-pattern-reason"
            label="Begründung"
            value={reason}
            onChange={setReason}
          />
        </FormGrid>

        <div style={{ paddingTop: "var(--sp-4)" }}>
          {failure === null ? (
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Ohne festgehaltenen Grund ist in sechs Monaten nicht mehr erklärbar, warum dieses
              Muster schweigt.
            </span>
          ) : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}
        </div>
      </Dialog>
    </Modal>
  );
}
